<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <report-meals-packages-add-meal
      v-if="selectedCompany"
      v-model="addMealDialog"
      :company="selectedCompany"
      @refreshMeal="loadMealsForRestaurantCompanies"
    >
    </report-meals-packages-add-meal>
    <print-layout-dialog v-model="printLayoutDialog" :branches="hasBranchGroups" @choose="printCompanies" />
    <v-row align="start">
      <v-col cols="8" lg="auto" class="d-flex align-center">
        <report-meals-select-companies></report-meals-select-companies>
      </v-col>
      <v-col cols="4" lg="auto" class="d-flex align-center">
        <shift-picker v-model="selectedShifts" multiple />
      </v-col>
      <v-col v-if="hasBranchGroups" cols="12" lg="auto" class="d-flex align-self-end">
        <v-tooltip bottom max-width="320">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              height="40"
              color="primary"
              :outlined="!sumBranches"
              :depressed="sumBranches"
              :aria-pressed="String(sumBranches)"
              @click="setSumBranches(!sumBranches)"
            >
              <v-icon left>mdi-sigma</v-icon>
              {{ $t("packing_sum_toggle") }}
            </v-btn>
          </template>
          <span>{{ $t("packing_sum_hint") }}</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <!-- Empty state: No companies selected -->
    <empty-state
      v-if="!selectedCompanies.length"
      icon="mdi-domain"
      :title="$t('no_companies_selected')"
      :subtitle="$t('select_companies_to_view_report')"
    />

    <!-- Loading state -->
    <empty-state v-else-if="isLoading" :title="$t('loading')" :loading="true" />

    <!-- Empty state: No data -->
    <empty-state
      v-else-if="!companies.length"
      icon="mdi-food-off"
      :title="$t('no_data')"
      :subtitle="$t('no_meals_for_selected_period')"
    />

    <!-- Company cards -->
    <v-card
      v-else
      flat
      class="company-card mb-6"
      outlined
      v-for="company in displayCompanies"
      :key="company.packingKey || company.name"
    >
      <v-card-title>
        <v-row align="center" no-gutters>
          <v-col cols="auto" v-if="filters.dates.from === filters.dates.to">
            <div style="width: 48px"></div>
            <!-- Spacer -->
          </v-col>

          <v-col>
            <div class="text-center">
              <!-- Company Name -->
              <div class="company-header mb-4">
                <h2 class="company-title">
                  <v-icon v-if="company.isGroup" class="mr-1">mdi-sigma</v-icon>
                  {{ toLatin(company.name) }}
                </h2>
                <div v-if="company.isGroup" class="text-body-2 grey--text text--darken-1">
                  {{ $t("packing_sum_members", { list: company.members.map((member) => toLatin(member)).join(", ") }) }}
                </div>
              </div>

              <!-- Company Details -->
              <div class="company-details">
                <div class="detail-item" v-if="company.address">
                  <v-icon small class="detail-icon">mdi-map-marker-outline</v-icon>
                  <span class="detail-text">{{ toLatin(company.address) }}</span>
                </div>

                <div
                  class="detail-separator"
                  v-if="company.address && (company.phone || company.city)"
                >
                  •
                </div>

                <div class="detail-item" v-if="company.phone">
                  <v-icon small class="detail-icon">mdi-phone-outline</v-icon>
                  <span class="detail-text">{{ company.phone }}</span>
                </div>

                <div class="detail-separator" v-if="company.phone && company.city">•</div>

                <div class="detail-item" v-if="company.city">
                  <v-icon small class="detail-icon">mdi-city-variant-outline</v-icon>
                  <span class="detail-text">{{ toLatin(company.city) }}</span>
                </div>
              </div>
            </div>
          </v-col>

          <v-col cols="auto" v-if="filters.dates.from === filters.dates.to && !company.isGroup">
            <primary-button @click="openAddDialog(company)">
              <v-icon class="mr-2">mdi-plus-circle</v-icon>
              {{ $t("add_meals2") }}
            </primary-button>
          </v-col>
        </v-row>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="getMobileHeaders(headers)"
          :items="customSearch(company.meals, headers, filters.search)"
          class="ordera-box-shadow striped-table"
          hide-default-footer
          disable-pagination
          mobile-breakpoint="0"
        >
          <template v-slot:item.name="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  {{ item.name }}
                </span>
              </template>
              <span>{{ item.id }}</span>
            </v-tooltip>
          </template>
          <template #body.append>
            <tr class="font-weight-bold totals-row">
              <td v-if="!device.isMobile"></td>
              <td>{{ $t("total") }}</td>
              <td v-for="shift in shiftsWithData" :key="shift" class="text-right">
                {{ formatShiftQuantity(company.totals[getShiftKey(shift)]) }}
              </td>
              <td class="text-right">{{ company.totals.total }}</td>
            </tr>
          </template>
          <!-- Dynamic shift templates -->
          <template
            v-for="shift in shiftsWithData"
            v-slot:[`item.${getShiftKey(shift)}`]="{ item }"
          >
            <span v-if="item[getShiftKey(shift)].customerQuantity > 0">
              {{ item[getShiftKey(shift)].customerQuantity }}
            </span>
            <span v-if="item[getShiftKey(shift)].restaurantQuantity > 0">
              <span v-if="item[getShiftKey(shift)].customerQuantity > 0"> + </span>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on"
                    >{{ item[getShiftKey(shift)].restaurantQuantity }}R</span
                  >
                </template>
                <span>{{ $t("ordered_by_restaurant") }}</span>
              </v-tooltip>
            </span>
            <span v-if="item[getShiftKey(shift)].totalQuantity === 0">0</span>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import ReportMealsPackagesAddMeal from "@/components/report/meals/dialogs/ReportMealsPackagesAddMeal.vue";
import ReportMealsSelectCompanies from "@/components/report/meals/fields/ReportMealsSelectCompaniesField.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";
import PrintLayoutDialog from "@/components/report/shared/PrintLayoutDialog.vue";
import { buildPackingUnits, sumPackingCompanies } from "@/components/report/shared/packingGroups";
import { SHIFTS, getShiftKey } from "@/constants/shifts";

const SUM_BRANCHES_KEY = "rm-packing-sum-branches";
const readSumBranches = () => {
  try {
    return localStorage.getItem(SUM_BRANCHES_KEY) === "1";
  } catch (error) {
    return false;
  }
};
const writeSumBranches = (value) => {
  try {
    localStorage.setItem(SUM_BRANCHES_KEY, value ? "1" : "0");
  } catch (error) {
    // bez localStorage-a izbor važi dok je ekran otvoren
  }
};

export default {
  name: "ReportMealsTabPackages",
  mixins: [hasPrinting, hasReportPrinting, hasExcel, hasSnackbar, hasMobileHeaders],
  components: {
    ReportMealsSelectCompanies,
    ReportMealsPackagesAddMeal,
    EmptyState,
    PrintLayoutDialog,
  },
  data() {
    return {
      report: [],
      isLoading: false,
      selectedCompany: null,
      addMealDialog: false,
      selectedShifts: SHIFTS.map((s) => s.id),
      printLayoutDialog: false,
      sumBranches: readSumBranches(),
    };
  },
  methods: {
    getShiftKey,

    /** Zbirni prikaz matičnih firmi sa poslovnicama na ekranu (pamti se na ovom uređaju). */
    setSumBranches(value) {
      this.sumBranches = value;
      writeSumBranches(value);
    },

    formatShiftQuantity(shiftData) {
      if (!shiftData) return "0";

      const {
        customerQuantity = 0,
        restaurantQuantity = 0,
        totalQuantity = 0,
      } = shiftData;

      if (totalQuantity === 0) return "0";

      let result = "";
      if (customerQuantity > 0) {
        result += customerQuantity.toString();
      }
      if (restaurantQuantity > 0) {
        if (customerQuantity > 0) result += " + ";
        result += `${restaurantQuantity}R`;
      }

      return result || "0";
    },

    prepareDataForExport(companies) {
      const activeShifts = SHIFTS.filter((shift) =>
        this.shiftsWithData.includes(shift.id)
      );

      return companies.map((company) => {
        const items = company.meals.map((meal) => ({
          ...meal,
          name: this.toLatin(meal.name),
          ...Object.fromEntries(
            activeShifts.map((shift) => [
              shift.key,
              this.formatShiftQuantity(meal[shift.key]),
            ])
          ),
        }));

        // Add totals row
        items.push({
          num: "",
          name: this.$t("totalU"),
          ...Object.fromEntries(
            activeShifts.map((shift) => [
              shift.key,
              this.formatShiftQuantity(company.totals[shift.key]),
            ])
          ),
          total: company.totals.total,
        });

        return {
          subtitle: this.generateTableSubtitle(),
          title: this.toLatin(company.name),
          items,
          beforeTableHtml: this.generateCompanyDetailsHtml(company),
        };
      });
    },

    generateTableSubtitle() {
      const fromDate = this.filters.dates.from;
      const toDate = this.filters.dates.to;

      if (fromDate === toDate) {
        return fromDate;
      } else {
        return `${fromDate} - ${toDate}`;
      }
    },

    generateCompanyDetailsHtml(company) {
      const details = [
        { key: "address", icon: "📍" },
        { key: "city", icon: "🏙️" },
        { key: "phone", icon: "📞" },
        { key: "email", icon: "📧" },
      ].filter((detail) => company[detail.key]);

      if (!details.length) return "";

      const detailsHtml = details
        .map(
          (detail) =>
            `<div style="display: flex; align-items: center; gap: 6px;">
          <span style="font-weight: 500;">${detail.icon}</span>
          <span>${company[detail.key]}</span>
        </div>`
        )
        .join("");

      return `<div style="margin-bottom: 20px; padding: 15px; background-color: #f8f9fa; border-left: 4px solid #007bff; font-family: Arial, sans-serif;">
        <div style="display: flex; justify-content: space-between; flex-wrap: wrap; gap: 20px; color: #546e7a; font-size: 14px;">
          ${detailsHtml}
        </div>
      </div>`;
    },

    openAddDialog(company) {
      this.selectedCompany = company;
      this.addMealDialog = true;
    },
    /**
     * Tabele štampe po jedinicama (firma ili matična firma sa poslovnicama).
     * "branches": poslovnice jedna iza druge na istom listu; "sum": poslovnice sabrane u jednu tabelu.
     * U oba rasporeda firme bez poslovnica i svaka grupa počinju na novom listu.
     */
    branchPrintTables(mode) {
      const tables = [];
      this.packingUnits.forEach((unit, index) => {
        let data;
        if (unit.type === "group" && mode === "sum") {
          const sum = sumPackingCompanies(unit.members, unit.name);
          const [table] = this.prepareDataForExport([sum]);
          data = [
            {
              ...table,
              title: this.$t("print_sum_title", { name: this.toLatin(unit.name) }),
              beforeTableHtml: this.sumNoteHtml(unit.members),
            },
          ];
        } else {
          data = this.prepareDataForExport(unit.type === "group" ? unit.members : [unit.company]);
        }
        data.forEach((table, position) => {
          tables.push({ ...table, breakBefore: index > 0 && position === 0 });
        });
      });
      return tables;
    },

    sumNoteHtml(members) {
      const names = members.map((member) => this.toLatin(member.name)).join(", ");
      return `<div style="margin-bottom: 20px; padding: 12px 15px; background-color: #f8f9fa; border-left: 4px solid #007bff; font-family: Arial, sans-serif; color: #546e7a; font-size: 14px;">${this.$t(
        "print_sum_note",
        { list: names }
      )}</div>`;
    },

    /**
     * Štampa liste za pakovanje svih firmi. layout: "separate" (ili true) svaka firma na novom listu;
     * "same" (ili false) firme jedna ispod druge bez preloma (tabela firme se ne cepa između listova);
     * "branches" / "sum": raspored za matične firme sa poslovnicama (vidi branchPrintTables).
     */
    printCompanies(layout) {
      const mode = layout === true ? "separate" : layout === false ? "same" : layout;
      const separatePages = mode === "separate";
      const groupedMode = mode === "branches" || mode === "sum";
      const exportData = groupedMode
        ? this.branchPrintTables(mode)
        : this.prepareDataForExport(this.companies);
      this.printDocument({
        tables: exportData.map((data) => ({
          ...data,
          headers: this.headers,
          title: data.title + " - " + this.subtitle,
          subtitle: [this.$t("report"), this.$t("pak")].join(": "),
          caption:
            this.$t("report_creation_datetime") +
            ": " +
            this.localeDate(new Date(), {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
            }),
        })),
        hideDatetime: true,
        // "branches" / "sum": prelom samo gde ga traži breakBefore, tabela firme se ne cepa između listova
        additionalStyles: separatePages
          ? `
            .print-table {
              page-break-after: always;
            }
            .print-table:last-child {
              page-break-after: auto;
            }
          `
          : `
            .print-table {
              page-break-inside: avoid;
              break-inside: avoid;
              margin-bottom: 18px;
            }
            .print-table-title {
              page-break-after: avoid;
              break-after: avoid;
            }
          `,
      });
    },
    async loadMealsForRestaurantCompanies() {
      if (!this.selectedCompanies || this.selectedCompanies.length === 0) {
        this.report = [];
        return;
      }

      this.isLoading = true;

      const params = {
        date: this.filters.dates.from,
        date2: this.filters.dates.to,
        company: this.selectedCompanies.map((company) => company.id).join(","),
      };

      try {
        this.report = await reportService.getPackagesForRestaurantCompanies(
          this.$store.state.restaurant.id,
          params
        );
      } catch (error) {
        this.report = [];
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.isLoading = false;
      }
    },
  },
  computed: {
    selectedCompanies() {
      return this.$store.state.report.selectedMealCompanies || [];
    },

    companies() {
      if (!this.report?.length) return [];

      const allShiftsSelected = this.selectedShifts.length === SHIFTS.length;

      // API already filters by selected companies, so we process all returned data
      return this.report.map((company) => {
        // Find company details from selected companies
        const companyDetails = this.selectedCompanies.find(
          (c) => c.name === company.name
        );

        if (allShiftsSelected) {
          return { ...company, ...companyDetails };
        }

        // Recalculate totals for filtered shifts
        const meals = company.meals.map((meal) => {
          let total = 0;

          SHIFTS.forEach((shift) => {
            if (this.selectedShifts.includes(shift.id)) {
              total += meal[shift.key]?.totalQuantity || 0;
            }
          });

          return { ...meal, total };
        });

        const totals = { total: 0 };
        SHIFTS.forEach((shift) => {
          totals[shift.key] = 0;

          if (this.selectedShifts.includes(shift.id)) {
            meals.forEach((meal) => {
              totals[shift.key] += meal[shift.key]?.totalQuantity || 0;
            });
          }
        });

        meals.forEach((meal) => {
          totals.total += meal.total;
        });

        return {
          ...company,
          ...companyDetails,
          meals,
          totals,
        };
      });
    },

    /** Firme izveštaja razvrstane u jedinice štampe: firma ili matična firma sa poslovnicama. */
    packingUnits() {
      return buildPackingUnits(
        this.companies,
        this.$store.state.report.mealCompanies || [],
        (text) => this.toLatin(text)
      );
    },
    hasBranchGroups() {
      return this.packingUnits.some((unit) => unit.type === "group");
    },
    /**
     * Firme za čeklistu Pakovanja: kad je uključen zbirni prikaz, matična firma i njene poslovnice su jedna
     * firma (zbir jela), ostale ostaju kakve jesu. `isGroup` firme se ne dopunjuju obrocima.
     */
    displayCompanies() {
      if (!this.sumBranches) {
        return this.companies;
      }
      return this.packingUnits.map((unit) =>
        unit.type === "group"
          ? {
              ...sumPackingCompanies(unit.members, unit.name),
              isGroup: true,
              packingKey: `Σ ${unit.name}`,
              members: unit.members.map((member) => member.name),
            }
          : unit.company
      );
    },

    shiftsWithData() {
      // Return only the selected shifts in their original order
      return this.selectedShifts.sort((a, b) => a - b);
    },
    headers() {
      const baseHeaders = [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("meal"), value: "name" },
      ];

      // Show headers only for selected shifts
      const shiftHeaders = SHIFTS.filter((shift) =>
        this.selectedShifts.includes(shift.id)
      ).map((shift) => ({
        text: this.$t(shift.label),
        value: shift.key,
        align: "right",
      }));

      const totalHeader = [{ text: this.$t("total"), value: "total", align: "right" }];

      return [...baseHeaders, ...shiftHeaders, ...totalHeader];
    },
    filters() {
      return this.$store.state.report.filters;
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.loadMealsForRestaurantCompanies();
      },
      deep: true,
    },
    selectedCompanies: {
      handler() {
        this.loadMealsForRestaurantCompanies();
      },
      immediate: true,
      deep: true,
    },
    isPrinting() {
      if (!this.isPrinting) {
        return;
      }
      // Više firmi: korisnik bira posebne listove ili sve firme na istom listu; jedna firma se štampa odmah.
      if (this.companies.length > 1) {
        this.printLayoutDialog = true;
      } else {
        this.printCompanies(true);
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        const exportData = this.prepareDataForExport(this.companies);
        this.downloadExcel({
          fileName: [this.$t("report"), this.$t("pak")].join(" ") + ".xlsx",
          sheets: exportData.map((data) => ({
            name: data.title,
            headers: this.headers,
            items: data.items,
            title: data.title,
            subtitle: this.subtitle,
          })),
        });
      }
    },
  },
};
</script>

<style scoped>
.company-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  background: linear-gradient(145deg, #ffffff 0%, rgba(0, 0, 0, 0.02) 100%);
}

.company-card:hover {
  border-color: rgba(25, 118, 210, 0.2);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}

/* Company Header */
.company-header {
  position: relative;
}

.company-title {
  font-size: 1.75rem;
  font-weight: 500;
  color: #2c3e50;
  margin: 0;
  letter-spacing: -0.5px;
}

/* Company Details */
.company-details {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
  color: #546e7a;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.detail-icon {
  opacity: 0.7;
  color: #607d8b !important;
}

.detail-text {
  font-size: 0.95rem;
  font-weight: 400;
}

.detail-separator {
  color: #bdc3c7;
  font-weight: bold;
  font-size: 0.8rem;
}

/* Add Button */
.add-btn {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

.add-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
}

/* Responsive Design */
@media (max-width: 768px) {
  .company-details {
    flex-direction: column;
    gap: 8px;
  }

  .detail-separator {
    display: none;
  }

  .company-title {
    font-size: 1.5rem;
  }
}

@media (max-width: 600px) {
  .company-card {
    border-radius: 8px;
  }

  .company-title {
    font-size: 1.3rem;
  }

  .detail-text {
    font-size: 0.9rem;
  }
}
</style>
